/**
 * Detect a custom script that fires Meta events the step's pixel block already fires.
 *
 * A step offers two ways to put a Meta pixel on the page: the Client-Side Pixels
 * form, where the merchant supplies an ID and ticks events, and the custom-scripts
 * box, where they paste Meta's own install snippet. Meta's instructions tell them
 * to paste the snippet, so a merchant who has done both ends up sending each sale
 * twice — and the pasted call carries no `eventID`, so Meta cannot deduplicate the
 * pair.
 *
 * Two shapes count as a conflict:
 *
 *  - `fbq('track', …)` is not addressed to a pixel. Meta delivers it to every
 *    pixel registered on the page, so it reaches the one the pixel form manages
 *    whatever ID the script itself initialised.
 *  - `fbq('init' | 'trackSingle' | 'trackSingleCustom', '<id>', …)` naming an ID
 *    the pixel form already manages, which is that same duplicate stated
 *    explicitly.
 *
 * KNOWN LIMIT — only the events the form can configure are covered.
 * `managedPixels[].events` carries what the merchant ticked, which the CRM schema
 * restricts to PageView / InitiateCheckout / Purchase. The SDK is broader:
 * `isEventEnabled` (packages/plugin-sdk/src/v2/core/pixelMapping.ts) returns true
 * for any event name ABSENT from that map, so the same pixel also fires
 * ViewContent, AddToCart, Lead… whenever a plugin calls trackEvent with them. A
 * pasted `fbq('track','ViewContent')` is therefore a real duplicate that this
 * detector stays silent about. Widening it to match would flag every merchant who
 * legitimately adds an event our storefront never emits, which is the more common
 * case — so the narrow rule is deliberate. Revisit together if the form ever
 * exposes more events.
 */

/** A Meta pixel entry as the step's tracking config stores it. */
export interface ManagedMetaPixel {
  pixelId: string;
  /** Event names the merchant ticked, e.g. ['Purchase']. */
  events: string[];
}

export interface MetaScriptConflict {
  /** Events the script re-fires into a pixel the step already manages. */
  duplicatedEvents: string[];
  /** Managed pixel IDs the script names, or initialises alongside a global track. */
  duplicatedPixelIds: string[];
}

/** Minimal shape of a step's custom script; avoids depending on the editor types. */
interface ScriptLike {
  enabled?: boolean;
  content?: string;
}

const GLOBAL_TRACK = /fbq\s*\(\s*['"]track['"]\s*,\s*['"]([A-Za-z0-9_]+)['"]/g;
const PIXEL_SCOPED = /fbq\s*\(\s*['"](?:init|trackSingle|trackSingleCustom)['"]\s*,\s*['"]?(\d+)['"]?/g;

function captures(pattern: RegExp, content: string): string[] {
  // String.prototype.matchAll clones the regex internally, so the module-level
  // /g patterns never carry `lastIndex` state between calls.
  return [...content.matchAll(pattern)].map((match) => match[1]);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}

/**
 * Returns what a script duplicates, or null when it conflicts with nothing.
 * `managedPixels` should carry only the enabled Meta pixels of the same step.
 */
export function findMetaScriptConflict(
  content: string | undefined,
  managedPixels: ManagedMetaPixel[],
): MetaScriptConflict | null {
  if (!content || managedPixels.length === 0) return null;
  // Cheap reject before two regex passes: this runs on every keystroke in the
  // editor, over script bodies that have no size cap.
  if (!content.includes('fbq')) return null;

  const managedIds = new Set(managedPixels.map((pixel) => pixel.pixelId));
  const managedEvents = new Set(managedPixels.flatMap((pixel) => pixel.events));

  const duplicatedEvents = unique(
    captures(GLOBAL_TRACK, content).filter((event) => managedEvents.has(event)),
  );
  const duplicatedPixelIds = unique(
    captures(PIXEL_SCOPED, content).filter((pixelId) => managedIds.has(pixelId)),
  );

  if (duplicatedEvents.length === 0 && duplicatedPixelIds.length === 0) return null;
  return { duplicatedEvents, duplicatedPixelIds };
}

/** Conflicts per script, index-aligned with `scripts`. Disabled scripts are never injected. */
export function findMetaScriptConflicts(
  scripts: ScriptLike[],
  managedPixels: ManagedMetaPixel[],
): (MetaScriptConflict | null)[] {
  return scripts.map((script) =>
    script.enabled ? findMetaScriptConflict(script.content, managedPixels) : null,
  );
}

/** How many scripts conflict — for the badge on a collapsed section header. */
export function countMetaScriptConflicts(scripts: ScriptLike[], managedPixels: ManagedMetaPixel[]): number {
  return findMetaScriptConflicts(scripts, managedPixels).filter(Boolean).length;
}
